/**
 * ProfileEditorTabs Component
 *
 * Tab navigation for the 5-tab profile editor:
 * 1. Basic - Name, contact, bio
 * 2. About - Specializations, languages, certifications
 * 3. Credentials - Licenses, insurance with expiration dates
 * 4. Photos - Profile photo and gallery
 * 5. Preview - Live preview of public profile
 *
 * Epic: MVP 1.21.3 - Guide Dashboard
 * Story: US-1.21.3-12: 5-Tab Profile Editor (8 SP)
 *
 * Props:
 * - activeTab: string - Current active tab ('basic', 'about', 'credentials', 'photos', 'preview')
 * - onChange: function(tabId) - Called when tab is clicked
 * - completedTabs: array of tab IDs that are completed (shows checkmark)
 */

import React from 'react';
import PropTypes from 'prop-types';
import { User as _User, FileText, Shield, Camera, Eye, Check } from 'lucide-react';

const tabs = [
  { id: 'basic', label: 'Basic Info', icon: _User },
  { id: 'about', label: 'About', icon: FileText },
  { id: 'credentials', label: 'Credentials', icon: Shield },
  { id: 'photos', label: 'Photos', icon: Camera },
  { id: 'preview', label: 'Preview', icon: Eye },
];

export default function ProfileEditorTabs({ activeTab, onChange, completedTabs = [] }) {
  return (
    <div className="border-b border-gray-200 bg-white">
      <nav className="flex space-x-8 px-6" aria-label="Profile editor tabs">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          const isCompleted = completedTabs.includes(tab.id);

          return (
            <button
              key={tab.id}
              onClick={() => onChange(tab.id)}
              className={`
                group inline-flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                ${
                  isActive
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon
                className={`w-5 h-5 ${
                  isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-500'
                }`}
              />
              <span>{tab.label}</span>
              {isCompleted && !isActive && (
                <Check className="w-4 h-4 text-green-600" />
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
}

ProfileEditorTabs.propTypes = {
  activeTab: PropTypes.oneOf(['basic', 'about', 'credentials', 'photos', 'preview']).isRequired,
  onChange: PropTypes.func.isRequired,
  completedTabs: PropTypes.arrayOf(PropTypes.string),
};
